import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useMemo } from 'react';
import FormItem from '../../Form/FormItem/FormItem';
import DropdownNativeSelect from './DropdownNativeSelect';
import DropdownSimpleSelect from './DropdownSimpleSelect';
import { isClient } from '../../helpers/accessibility';
import { useInOut, useInput, useIsClient, useMobileFeature } from '../../hooks';
import { getValue } from './helpers/value';
var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$useNativeSelectF = _ref.useNativeSelectForMobile,
      useNativeSelectForMobile = _ref$useNativeSelectF === void 0 ? true : _ref$useNativeSelectF,
      _ref$forceNativeSelec = _ref.forceNativeSelect,
      forceNativeSelect = _ref$forceNativeSelec === void 0 ? false : _ref$forceNativeSelec,
      _ref$hideErrorOnFocus = _ref.hideErrorOnFocus,
      hideErrorOnFocus = _ref$hideErrorOnFocus === void 0 ? true : _ref$hideErrorOnFocus,
      _ref$withBottomMargin = _ref.withBottomMargin,
      withBottomMargin = _ref$withBottomMargin === void 0 ? false : _ref$withBottomMargin,
      _ref$withTopMargin = _ref.withTopMargin,
      withTopMargin = _ref$withTopMargin === void 0 ? false : _ref$withTopMargin,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      onMenuOpen = _ref.onMenuOpen,
      onMenuClose = _ref.onMenuClose,
      value = _ref.value,
      defaultValue = _ref.defaultValue,
      onChange = _ref.onChange,
      error = _ref.error,
      errorProps = _ref.errorProps,
      _ref$withAbsoluteErro = _ref.withAbsoluteError,
      withAbsoluteError = _ref$withAbsoluteErro === void 0 ? false : _ref$withAbsoluteErro,
      _ref$withNoPaddingFor = _ref.withNoPaddingForError,
      withNoPaddingForError = _ref$withNoPaddingFor === void 0 ? false : _ref$withNoPaddingFor,
      label = _ref.label,
      className = _ref.className,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$notClearable = _ref.notClearable,
      notClearable = _ref$notClearable === void 0 ? false : _ref$notClearable,
      _ref$notSearchable = _ref.notSearchable,
      notSearchable = _ref$notSearchable === void 0 ? false : _ref$notSearchable,
      _ref$blurOnSelect = _ref.blurOnSelect,
      blurOnSelect = _ref$blurOnSelect === void 0 ? false : _ref$blurOnSelect,
      icon = _ref.icon,
      iconRight = _ref.iconRight,
      additionalIndicator = _ref.additionalIndicator,
      additionalIndicatorProps = _ref.additionalIndicatorProps,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      _ref$fullWidth = _ref.fullWidth,
      fullWidth = _ref$fullWidth === void 0 ? false : _ref$fullWidth,
      _ref$useVirtualizedMe = _ref.useVirtualizedMenuList,
      useVirtualizedMenuList = _ref$useVirtualizedMe === void 0 ? false : _ref$useVirtualizedMe,
      _ref$hideInput = _ref.hideInput,
      hideInput = _ref$hideInput === void 0 ? false : _ref$hideInput,
      renderOptionIcon = _ref.renderOptionIcon,
      renderOptionImage = _ref.renderOptionImage,
      minMenuHeight = _ref.minMenuHeight,
      maxMenuHeight = _ref.maxMenuHeight,
      maxHeight = _ref.maxHeight,
      minOptionsCount = _ref.minOptionsCount,
      maxOptionsCount = _ref.maxOptionsCount,
      components = _ref.components,
      inputId = _ref.inputId,
      selectId = _ref.selectId,
      props = _objectWithoutPropertiesLoose(_ref, ["multiple", "useNativeSelectForMobile", "forceNativeSelect", "hideErrorOnFocus", "withBottomMargin", "withTopMargin", "onBlur", "onFocus", "onMenuOpen", "onMenuClose", "value", "defaultValue", "onChange", "error", "errorProps", "withAbsoluteError", "withNoPaddingForError", "label", "className", "options", "disabled", "notClearable", "notSearchable", "blurOnSelect", "icon", "iconRight", "additionalIndicator", "additionalIndicatorProps", "placeholder", "fullWidth", "useVirtualizedMenuList", "hideInput", "renderOptionIcon", "renderOptionImage", "minMenuHeight", "maxMenuHeight", "maxHeight", "minOptionsCount", "maxOptionsCount", "components", "inputId", "selectId"]);

  var _useInput = useInput(value, defaultValue != null ? defaultValue : multiple ? [] : null, onChange, multiple),
      internalValue = _useInput[0],
      setInternalValue = _useInput[1];

  var _useInOut = useInOut(onFocus, onBlur),
      isFocused = _useInOut[0],
      handleFocus = _useInOut[1],
      handleBlur = _useInOut[2];

  var _useInOut2 = useInOut(onMenuOpen, onMenuClose),
      isMenuOpen = _useInOut2[0],
      handleMenuOpen = _useInOut2[1],
      handleMenuClose = _useInOut2[2];

  var useNativeSelect = useMobileFeature(useNativeSelectForMobile, forceNativeSelect);
  var displayedValue = useMemo(function () {
    return getValue(options, internalValue, multiple, useNativeSelect);
  }, [options, internalValue, multiple, useNativeSelect]);

  var _useIsClient = useIsClient('dropdown'),
      key = _useIsClient.key;

  return /*#__PURE__*/React.createElement(FormItem, {
    key: key,
    id: props.id,
    name: props.name,
    label: label,
    error: error,
    errorProps: errorProps,
    className: className,
    showError: !isFocused || !hideErrorOnFocus,
    visibleError: !isMenuOpen,
    absoluteError: withAbsoluteError,
    withBottomMargin: withBottomMargin,
    withTopMargin: withTopMargin,
    withNoPaddingForError: withNoPaddingForError,
    fullWidth: fullWidth
  }, useNativeSelect || !isClient() ? /*#__PURE__*/React.createElement(DropdownNativeSelect, _extends({
    ref: forwardedRef,
    multiple: multiple,
    disabled: disabled,
    error: error,
    options: options,
    placeholder: placeholder,
    icon: icon,
    iconRight: iconRight,
    additionalIndicator: additionalIndicator,
    additionalIndicatorProps: additionalIndicatorProps
    /* @ts-expect-error `onFocus` prop mismatch */
    ,
    onFocus: handleFocus
    /* @ts-expect-error `onBlur` prop mismatch */
    ,
    onBlur: handleBlur,
    value: displayedValue,
    onValueChange: function onValueChange(newInternalValue) {
      if (disabled) return;

      if (!multiple) {
        if (notClearable && !newInternalValue) {
          return;
        }

        setInternalValue(newInternalValue);
      } else {
        setInternalValue(newInternalValue || []);
      }
    },
    blurOnSelect: blurOnSelect,
    notClearable: notClearable,
    hideErrorOnFocus: hideErrorOnFocus
  }, props, {
    id: selectId != null ? selectId : props.id
  })) : /*#__PURE__*/React.createElement(DropdownSimpleSelect, _extends({
    ref: forwardedRef,
    options: options,
    error: error,
    value: displayedValue,
    blurInputOnSelect: blurOnSelect,
    onChange: function onChange(newInternalValue) {
      if (disabled) return;

      if (!multiple) {
        var _value = Array.isArray(newInternalValue) ? newInternalValue[0] : newInternalValue;

        if (notClearable && !_value) {
          return;
        }

        setInternalValue(_value == null ? void 0 : _value.value);
      } else {
        setInternalValue((newInternalValue || []).map(function (v) {
          return v.value;
        }));
      }
    },
    onFocus: handleFocus,
    onBlur: handleBlur,
    isMulti: multiple,
    isDisabled: disabled,
    isClearable: !notClearable,
    isSearchable: !notSearchable,
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
    placeholder: placeholder,
    icon: icon,
    iconRight: iconRight,
    additionalIndicator: additionalIndicator,
    additionalIndicatorProps: additionalIndicatorProps,
    useVirtualizedMenuList: useVirtualizedMenuList,
    hideInput: hideInput,
    components: components,
    renderOptionIcon: renderOptionIcon,
    renderOptionImage: renderOptionImage,
    minMenuHeight: minMenuHeight,
    maxMenuHeight: maxMenuHeight,
    maxHeight: maxHeight,
    minOptionsCount: minOptionsCount,
    maxOptionsCount: maxOptionsCount,
    hideErrorOnFocus: hideErrorOnFocus,
    inputId: inputId
  }, props)));
});
export default Dropdown;